/**
 * 功能开关。
 *
 * ★ 一个开关只有同时具备三样东西才算数:
 *
 *   1. **一个默认值**,而且默认值要保守 —— 新功能默认关。
 *      默认开的话,升级一次系统就等于给所有租户上线了一个他们没要过的功能
 *   2. **一句「关掉之后会怎样」**。运营看到「地理围栏」四个字不知道该不该开,
 *      看到「关掉后司机在任何位置都能确认取货」就知道了
 *   3. **后端真的检查它**。只在界面上藏起来的开关不是开关,
 *      是一个还没被人发现的接口
 *
 * 这份注册表是唯一的清单:加开关就往这里加一行,别处只引用 key。
 * 散在各处的 `if (features.xxx)` 迟早会出现拼写不一致的那一个。
 */

export interface FeatureDef {
  key: string;
  label: string;
  /** 开着的时候是什么样 */
  description: string;
  /** ★ 关掉之后会怎样 —— 这句话比 label 重要 */
  whenOff: string;
  /** 默认值。新功能一律默认关 */
  default: boolean;
  group: '订单' | '结算' | '司机' | '对接';
}

export const FEATURES: readonly FeatureDef[] = [
  {
    key: 'inTransitStatus',
    label: '「配送中」状态',
    description: '司机取货后可以再点一次「开始配送」,收件人能看到更细的进度',
    whenOff: '取货之后直接就是配送状态,司机少点一次',
    default: false,
    group: '订单',
  },
  {
    key: 'settlementPenalty',
    label: '结算扣款',
    description: '结算单上可以加罚款项,从司机的应付金额里扣',
    whenOff: '结算单只能加奖励和调整,加不了罚款 —— 已经加过的不受影响',
    default: false,
    group: '结算',
  },
  {
    key: 'settlementSelfPay',
    label: '确认和打款可以同一个人做',
    description:
      '开了之后,能管理结算单的人也能标记已付款 —— 一个人的小队用得上',
    /*
     * ★ 默认关,是因为「算钱的人不经手付钱」是财务上最基本的一条:
     *   同一个人既能改金额又能标记已付,做手脚不需要第二个人配合,
     *   而对账时看到的只有一条「已付款」。
     *
     *   但默认关不等于挡死:一个人的车队里根本没有第二个人,
     *   而原来的做法是**谁也点不了那个按钮** —— 那不是安全,是坏了。
     */
    whenOff: '标记已付款要单独的财务账号(带 settlement:pay 的角色)',
    default: false,
    group: '结算',
  },
  {
    key: 'settlementBonus',
    label: '结算奖励',
    description: '结算单上可以加奖励项',
    whenOff: '结算单只按配送费结,加不了奖励',
    default: true,
    group: '结算',
  },
  {
    key: 'assignWithoutAccept',
    label: '派单免确认',
    description: '调度派单之后直接进入「已接单」,司机不用再点一次确认',
    whenOff: '派单后要司机自己点「接单」;超时没点的单会自动回到待派',
    default: false,
    group: '司机',
  },
  {
    key: 'grabPool',
    label: '抢单池',
    description: '司机可以在订单池里主动领单,不必等调度指派',
    whenOff: '所有单都由调度指派,司机端的「可领取」里永远是空的',
    // ★ 默认开。关着的时候司机端那一栏永远是空的,而没人看得出是开关的事 ——
    //   「怎么没有可领的单」这个问题被问了两次,两次都是它
    default: true,
    group: '司机',
  },
  {
    key: 'autoPricing',
    label: '自动计价',
    description: '建单时按计价规则自动算出运费和司机酬劳,不用手工填',
    whenOff: '运费和司机酬劳留空,由人工在建单时填 —— 已经算好的单不受影响',
    default: true,
    group: '订单',
  },
  {
    key: 'strictCheckOut',
    label: '手上有货不让下班',
    description: '司机手上还有已取货、未送达的单时,不允许签退',
    whenOff: '随时可以签退 —— 手上那几单还挂在他名下,由调度自己盯',
    default: false,
    group: '司机',
  },
  {
    key: 'smsNotify',
    label: '短信通知收件人',
    description: '取件和送达时给收件人发一条短信,带追踪链接',
    whenOff: '不发短信 —— 收件人只能自己打电话问,或者凭单号去追踪页查',
    default: true,
    group: '订单',
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp 按钮',
    description: '收件人电话旁边多一个 WhatsApp 按钮,点了直接开对话',
    whenOff: '只留打电话 —— 少一个长得一样的按钮,路上不容易按错',
    default: false,
    group: '司机',
  },
  {
    key: 'deliverOnly',
    label: '只显示「送达」',
    description:
      '订单上的按钮只有「送达」,点一下直接进送达面板 —— 少一次「送达还是未送达」的选择。' +
      '送不到的路径挪到面板底部一行小字',
    whenOff: '按钮是「送达 / 未送达」,点开先选结果 —— 两个结果同等大小,谁都不容易被漏掉',
    default: false,
    group: '司机',
  },
  {
    key: 'confirmPrechecked',
    label: '确认项默认勾上',
    description:
      '代收货款、特殊物品这些确认项打开面板时就是勾上的,司机直接拖动完成',
    whenOff: '每一项都要司机自己勾 —— 慢一点,但「钱收了吗」「冻品给了吗」是他真的看过一眼才勾的',
    default: false,
    group: '司机',
  },
  {
    key: 'specialItems',
    label: '特殊物品',
    description:
      '建单时可以给订单打上「冻品」「易碎」这类标记(分类自己在设置里加);' +
      '司机在订单上会看到高亮提醒,送达时要逐项确认已经交出去',
    whenOff: '建单时看不到这一栏 —— 已经打过标记的单还是会照常提醒司机,只是不能再打新的',
    default: false,
    group: '订单',
  },
  {
    key: 'cod',
    label: '代收货款',
    description: '订单可以带代收金额,司机送达时要记录收款',
    whenOff: '订单上不出现代收字段,送达时不问收款',
    default: true,
    group: '订单',
  },
  {
    key: 'openApi',
    label: '第三方下单接口',
    description:
      '商家自己的系统可以用 AppID + Secret 签名直接下单、查单(每个商家在「商家」设置里单独开)',
    whenOff: '所有商家的接口调用一律被拒绝 —— 商家上配好的 AppID / Secret 保留,重新打开就能接着用',
    default: false,
    group: '对接',
  },
  {
    key: 'orderWebhook',
    label: '订单状态推送',
    description: '订单建好、状态变化时,推送到商家配置的通知地址(每个商家在「商家」设置里单独配)',
    whenOff: '不再往任何商家推送 —— 已经排队还没发出去的也不发',
    default: false,
    group: '对接',
  },
] as const;

export type FeatureKey = (typeof FEATURES)[number]['key'];

const BY_KEY = new Map(FEATURES.map((f) => [f.key, f]));

/**
 * 这个开关开着吗。
 *
 * ★ 认不出的 key 一律返回 false,不是 true。
 *   打错一个字母就把功能默默打开,是最难查的一类问题 ——
 *   代码看起来在检查,实际上永远通过。
 */
export function isFeatureOn(features: unknown, key: string): boolean {
  const def = BY_KEY.get(key);
  if (!def) return false;
  const map = (features ?? {}) as Record<string, unknown>;
  const v = map[key];
  return typeof v === 'boolean' ? v : def.default;
}

/** 把存的值和默认值合成一份完整的开关表 —— 界面上要显示每一个 */
export function resolveFeatures(features: unknown): Array<FeatureDef & { enabled: boolean }> {
  return FEATURES.map((f) => ({ ...f, enabled: isFeatureOn(features, f.key) }));
}

/** 过滤掉不认识的 key —— 别让人往 features 里塞任意 JSON */
export function sanitizeFeatures(input: Record<string, unknown>): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(input)) {
    if (BY_KEY.has(k) && typeof v === 'boolean') out[k] = v;
  }
  return out;
}
